import { debounce } from 'lodash';
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { FilterContext } from 'utils/context';

import { Box, IconButton, reactIcons as Icons } from '@project44/manifest-ds';
import { Input } from '@project44/manifest-ui-deprecated';

interface IFilterInput {
  id: string;
  placeholder: string;
  doSearch: () => void;
}

const FilterInput = ({ id, placeholder, doSearch }: IFilterInput) => {
  const { filters, setFiltersKey } = useContext(FilterContext);
  const filter = (filters[id] as string) || '';
  const [searchTerm, setSearchTerm] = useState(filter);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleFetch = useCallback(
    debounce((value: string) => {
      setFiltersKey(id, value);
      doSearch();
    }, 400),
    [id, setFiltersKey, doSearch],
  );

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchTerm(value);
    handleFetch(value);
  };

  const handleClearInput = () => {
    setSearchTerm('');
    setFiltersKey(id, '');
    doSearch();
  };

  useEffect(() => {
    setSearchTerm(filter);
  }, [filter]);

  useEffect(() => {
    return () => {
      handleFetch.cancel();
    };
  }, [handleFetch]);

  return (
    <Box css={{ bgColor: 'white' }}>
      <Input
        data-testid="search-text"
        onChange={handleChangeInput}
        placeholder={placeholder}
        value={searchTerm}
        maxLength={200}
        startIcon={
          <Icons.Search
            css={{
              color: !!searchTerm ? '$primary-default' : '$text-disabled',
            }}
          />
        }
        endIcon={
          <IconButton
            children={<Icons.Close />}
            onClick={handleClearInput}
            variant="tertiary"
            css={{
              pointerEvents: 'auto',
              visibility: !!searchTerm ? '' : 'hidden',
            }}
          />
        }
      />
    </Box>
  );
};

export default FilterInput;
